import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Shield, Trash2, Search, AlertTriangle, CheckCircle, 
  Loader2, Database, Users, FileText, Video, Music,
  Calendar, Swords, RefreshCw
} from 'lucide-react';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';

export default function AdminDataHygienePage() {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [scanning, setScanning] = useState(false);
  const [purging, setPurging] = useState(false);
  const [scanResults, setScanResults] = useState(null);
  const [confirmPhrase, setConfirmPhrase] = useState('');
  const [showPurgeConfirm, setShowPurgeConfirm] = useState(false);
  const [purgeResults, setPurgeResults] = useState(null);

  const CONFIRM_PHRASE = 'DELETE TEST DATA';

  // Check admin access
  if (!user || (user.role !== 'owner' && user.role !== 'admin')) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="p-6 text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Access Denied</h2>
          <p className="text-muted-foreground mb-4">Platform Owner access required.</p>
          <Button onClick={() => navigate('/')}>Go Home</Button>
        </Card>
      </div>
    );
  }

  const handleScan = async () => {
    setScanning(true);
    setScanResults(null);
    setPurgeResults(null);
    
    try {
      const response = await fetch(`${API}/admin/data-hygiene/scan`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setScanResults(data);
        toast.success('Scan complete');
      } else {
        toast.error('Scan failed');
      }
    } catch (error) {
      toast.error('Scan error: ' + error.message);
    } finally {
      setScanning(false);
    }
  };

  const handlePurge = async () => {
    if (confirmPhrase !== CONFIRM_PHRASE) {
      toast.error(`Please type "${CONFIRM_PHRASE}" to confirm`);
      return;
    }

    setPurging(true);
    
    try {
      const response = await fetch(`${API}/admin/data-hygiene/purge`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirmPhrase: CONFIRM_PHRASE })
      });
      
      if (response.ok) {
        const data = await response.json();
        setPurgeResults(data);
        setScanResults(null);
        setShowPurgeConfirm(false);
        setConfirmPhrase('');
        toast.success('Test data purged successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Purge failed');
      }
    } catch (error) {
      toast.error('Purge error: ' + error.message);
    } finally {
      setPurging(false);
    }
  };

  const hasTestData = scanResults && (
    scanResults.test_users > 0 ||
    scanResults.test_posts > 0 ||
    scanResults.test_reels > 0 ||
    scanResults.test_tracks > 0 ||
    scanResults.test_interviews > 0 ||
    scanResults.test_events > 0 ||
    scanResults.test_battles > 0
  );

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-red-500/20 flex items-center justify-center">
            <Shield className="w-6 h-6 text-red-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Data Hygiene</h1>
            <p className="text-muted-foreground">Scan and remove test/demo data</p>
          </div>
        </div>

        {/* Warning Banner */}
        <Card className="p-4 border-red-500/30 bg-red-500/5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
            <div>
              <p className="font-medium text-red-400">Destructive Operations</p>
              <p className="text-sm text-muted-foreground">
                Purging test data permanently deletes accounts, posts, tracks, and all related content.
                This action cannot be undone.
              </p>
            </div>
          </div>
        </Card>

        {/* Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <Search className="w-5 h-5 text-blue-500" />
              <h2 className="font-bold">Scan for Test Data</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Scan the database for test accounts and content without making any changes.
            </p>
            <Button 
              onClick={handleScan} 
              disabled={scanning}
              className="w-full"
              variant="outline"
            >
              {scanning ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Scanning...
                </>
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" />
                  Scan for Test Data
                </>
              )}
            </Button>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <Trash2 className="w-5 h-5 text-red-500" />
              <h2 className="font-bold">Purge Test Data</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Permanently delete all test accounts and their content.
            </p>
            <Button 
              onClick={() => setShowPurgeConfirm(true)}
              disabled={purging || !scanResults}
              className="w-full"
              variant="destructive"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Purge Test Data
            </Button>
            {!scanResults && (
              <p className="text-xs text-muted-foreground mt-2 text-center">
                Run a scan first to see what will be deleted
              </p>
            )}
          </Card>
        </div>

        {/* Scan Results */}
        {scanResults && (
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold flex items-center gap-2">
                <Database className="w-5 h-5" />
                Scan Results
              </h2>
              {hasTestData ? (
                <Badge className="bg-red-500/20 text-red-400">Test Data Found</Badge>
              ) : (
                <Badge className="bg-green-500/20 text-green-400">Clean</Badge>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <ResultCard 
                icon={Users} 
                label="Test Users" 
                count={scanResults.test_users}
                hasData={scanResults.test_users > 0}
              />
              <ResultCard 
                icon={FileText} 
                label="Test Posts" 
                count={scanResults.test_posts}
                hasData={scanResults.test_posts > 0}
              />
              <ResultCard 
                icon={Video} 
                label="Test Clips" 
                count={scanResults.test_reels}
                hasData={scanResults.test_reels > 0}
              />
              <ResultCard 
                icon={Music} 
                label="Test Tracks" 
                count={scanResults.test_tracks}
                hasData={scanResults.test_tracks > 0}
              />
              <ResultCard 
                icon={FileText} 
                label="Test Interviews" 
                count={scanResults.test_interviews}
                hasData={scanResults.test_interviews > 0}
              />
              <ResultCard 
                icon={Calendar} 
                label="Test Events" 
                count={scanResults.test_events}
                hasData={scanResults.test_events > 0}
              />
              <ResultCard 
                icon={Swords} 
                label="Test Battles" 
                count={scanResults.test_battles}
                hasData={scanResults.test_battles > 0}
              />
            </div>

            <div className="mt-4 pt-4 border-t border-border">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total Users:</span>
                <span>{scanResults.total_users}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Scanned At:</span>
                <span>{new Date(scanResults.scanned_at).toLocaleString()}</span>
              </div>
            </div>
          </Card>
        )}

        {/* Purge Results */}
        {purgeResults && (
          <Card className="p-6 border-green-500/30 bg-green-500/5">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <h2 className="font-bold text-green-400">Purge Complete</h2>
            </div>

            <div className="space-y-2 text-sm">
              {Object.entries(purgeResults.deleted).map(([key, count]) => (
                count > 0 && (
                  <div key={key} className="flex justify-between">
                    <span className="text-muted-foreground capitalize">{key.replace(/_/g, ' ')}:</span>
                    <span className="text-green-400">{count} deleted</span>
                  </div>
                )
              ))}
            </div>

            <div className="mt-4 pt-4 border-t border-border text-xs text-muted-foreground">
              Purged at: {new Date(purgeResults.purged_at).toLocaleString()}
            </div>
          </Card>
        )}

        {/* Purge Confirmation Modal */}
        {showPurgeConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4">
            <Card className="max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="w-8 h-8 text-red-500" />
                <h2 className="text-xl font-bold">Confirm Purge</h2>
              </div>

              <p className="text-muted-foreground mb-4">
                This will permanently delete:
              </p>

              {scanResults && (
                <ul className="text-sm space-y-1 mb-4 pl-4">
                  {scanResults.test_users > 0 && <li>• {scanResults.test_users} test users</li>}
                  {scanResults.test_posts > 0 && <li>• {scanResults.test_posts} test posts</li>}
                  {scanResults.test_reels > 0 && <li>• {scanResults.test_reels} test reels</li>}
                  {scanResults.test_tracks > 0 && <li>• {scanResults.test_tracks} test tracks</li>}
                  {scanResults.test_interviews > 0 && <li>• {scanResults.test_interviews} test interviews</li>}
                  {scanResults.test_events > 0 && <li>• {scanResults.test_events} test events</li>}
                  {scanResults.test_battles > 0 && <li>• {scanResults.test_battles} test battles</li>}
                </ul>
              )}

              <div className="space-y-3">
                <p className="text-sm font-medium">
                  Type <span className="text-red-400 font-mono">{CONFIRM_PHRASE}</span> to confirm:
                </p>
                <Input
                  value={confirmPhrase}
                  onChange={(e) => setConfirmPhrase(e.target.value)}
                  placeholder="Type confirmation phrase"
                  className="font-mono"
                />
              </div>

              <div className="flex gap-3 mt-6">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    setShowPurgeConfirm(false);
                    setConfirmPhrase('');
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  variant="destructive" 
                  className="flex-1"
                  onClick={handlePurge}
                  disabled={confirmPhrase !== CONFIRM_PHRASE || purging}
                >
                  {purging ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Purging...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Purge Now
                    </>
                  )}
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Info Section */}
        <Card className="p-6">
          <h3 className="font-bold mb-3">What counts as test data?</h3>
          <ul className="text-sm text-muted-foreground space-y-2">
            <li>• Emails containing: test, demo, sample, fake, seed, or @example.com</li>
            <li>• Handles containing: test, demo, sample, placeholder</li>
            <li>• Records with isTest=true or createdBySeed=true flags</li>
            <li>• All content created by test accounts (posts, tracks, videos, etc.)</li>
          </ul>
          <p className="text-xs text-muted-foreground mt-4">
            Protected accounts (platform owners) are never deleted regardless of email pattern.
          </p>
        </Card>
      </div>
    </div>
  );
}

function ResultCard({ icon: Icon, label, count, hasData }) {
  return (
    <div className={`p-3 rounded-lg ${hasData ? 'bg-red-500/10 border border-red-500/30' : 'bg-muted/30'}`}>
      <Icon className={`w-5 h-5 mb-2 ${hasData ? 'text-red-500' : 'text-muted-foreground'}`} />
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-xl font-bold ${hasData ? 'text-red-400' : ''}`}>{count}</p>
    </div>
  );
}
